import {
  moveStart,
  startRange,
  withMonth,
  type CategoryKind,
  type Item,
  type ItemId,
  type KeyDateRef,
  type MonthIndex,
} from '@finsim/model';
import { useTranslation } from 'react-i18next';
import { KeyDateMonthField } from '../../../entities/project';
import { Button, Field, Input, Segmented, Select, useReadOnly } from '../../../shared/ui';
import type { FormContext } from './context';

const NONE = '__none';

/** Category picker filtered to the item's side (income or expense). */
export function CategoryField({
  ctx,
  value,
  kind,
  onChange,
}: {
  ctx: FormContext;
  value: string | undefined;
  kind: CategoryKind | null;
  onChange: (category: string | undefined) => void;
}) {
  const { t } = useTranslation();
  const categories = ctx.project.categories.filter(
    (c) => kind === null || c.kind === kind || c.id === value,
  );
  return (
    <Field label={t('fields.category')}>
      {({ id }) => (
        <Select
          id={id}
          value={value ?? NONE}
          options={[
            { value: NONE, label: t('fields.noCategory') },
            ...categories.map((c) => ({ value: c.id, label: c.name })),
          ]}
          onValueChange={(next) => {
            onChange(next === NONE ? undefined : next);
          }}
        />
      )}
    </Field>
  );
}

/** Transfer target: an asset account in the same variant, or none. */
export function AccountField({
  ctx,
  value,
  onChange,
}: {
  ctx: FormContext;
  value: ItemId | undefined;
  onChange: (account: ItemId | undefined) => void;
}) {
  const { t } = useTranslation();
  const scenario = ctx.project.scenarios.find((s) => s.id === ctx.scenarioId);
  const accounts = (scenario?.itemIds ?? [])
    .map((id) => ctx.project.items[id])
    .filter((i): i is Extract<Item, { type: 'asset' }> => i?.type === 'asset');
  if (accounts.length === 0 && value === undefined) return null;
  return (
    <Field label={t('fields.account')}>
      {({ id }) => (
        <Select
          id={id}
          value={value ?? NONE}
          options={[
            { value: NONE, label: t('fields.noAccount') },
            ...accounts.map((a) => ({ value: a.id, label: a.name })),
          ]}
          onValueChange={(next) => {
            onChange(next === NONE ? undefined : next);
          }}
        />
      )}
    </Field>
  );
}

export function DirectionField({
  value,
  onChange,
}: {
  value: 'in' | 'out';
  onChange: (v: 'in' | 'out') => void;
}) {
  const { t } = useTranslation();
  return (
    <Field label={t('fields.direction')}>
      {() => (
        <Segmented
          label={t('fields.direction')}
          value={value}
          onValueChange={onChange}
          options={[
            { value: 'in', label: t('fields.directionIn') },
            { value: 'out', label: t('fields.directionOut') },
          ]}
        />
      )}
    </Field>
  );
}

interface Windowed {
  start: MonthIndex;
  end?: MonthIndex | undefined;
  anchors?: { start?: KeyDateRef | undefined; end?: KeyDateRef | undefined } | undefined;
}

/**
 * Start month, plus an optional end month; either may be tied to a key date. Moving the start
 * takes the item's own dates along (`moveStart`).
 */
export function WindowFields<T extends Item & Windowed>({
  ctx,
  item,
  startLabel,
  showEnd = true,
  onChange,
}: {
  ctx: FormContext;
  item: T;
  startLabel?: string;
  showEnd?: boolean;
  onChange: (next: T, field: 'start' | 'end') => void;
}) {
  const { t } = useTranslation();
  const readOnly = useReadOnly();
  const { start: startMonth, horizonMonths } = ctx.project.assumptions;
  const max = horizonMonths - 1;
  const { start, end } = item;
  const [startMin, startMax] = startRange(item, max);
  return (
    <>
      <Field label={startLabel ?? t('fields.start')}>
        {({ id }) => (
          <KeyDateMonthField
            id={id}
            aria-label={startLabel ?? t('fields.start')}
            value={start}
            anchor={item.anchors?.start}
            start={startMonth}
            // Tied ends, sales, instalments and early repayments stay put: the start can't pass them.
            min={startMin}
            max={startMax}
            onCommit={(next, anchor) => {
              onChange(withMonth(moveStart(item, next), 'start', next, anchor), 'start');
            }}
          />
        )}
      </Field>
      {showEnd && (
        <Field label={t('fields.end')}>
          {({ id }) =>
            end === undefined && readOnly ? (
              <Input id={id} readOnly value={t('fields.noEnd')} />
            ) : end === undefined ? (
              <Button
                id={id}
                size="md"
                variant="ghost"
                className="w-full justify-start px-2 text-text-3"
                onClick={() => {
                  onChange(withMonth(item, 'end', Math.min(max, start + 11) as MonthIndex), 'end');
                }}
              >
                {t('fields.noEnd')}
              </Button>
            ) : (
              <div className="space-y-1">
                <KeyDateMonthField
                  id={id}
                  aria-label={t('fields.end')}
                  value={end}
                  anchor={item.anchors?.end}
                  start={startMonth}
                  min={start}
                  max={max}
                  onCommit={(next, anchor) => {
                    onChange(withMonth(item, 'end', next, anchor), 'end');
                  }}
                />
                {!readOnly && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      onChange(withMonth(item, 'end', undefined), 'end');
                    }}
                  >
                    {t('fields.clearEnd')}
                  </Button>
                )}
              </div>
            )
          }
        </Field>
      )}
    </>
  );
}
